import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { AxiosResponse } from "axios";
import React, { useEffect, useRef, useState } from "react";
import FacilityService from "../../Services/FacilityService/FacilityService";
import { Loader } from "../../Shared/Common/Loader";
import { ArrowDown, ArrowUp } from "../../Shared/Icons";
import { SortingTypeI, sortById } from "../../Utils/consts";
import Row from "./Row";
import useLang from "Shared/hooks/useLanguage";
import usePagination from "Shared/hooks/usePagination";
import CustomPagination from "Shared/JsxPagination";

export default function PhysicianSignatureGrid() {
  const { t } = useLang();
  const searchRef = useRef<any>(null);

  const initialValues = {
    firstName: "",
    lastName: "",
    npiNumber: "",
  };

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>(() => []);
  const [initialRender, setinitialRender] = useState(false);
  const [sort, setSorting] = useState<SortingTypeI>(sortById);
  const [searchRequest, setSearchRequest] = useState(initialValues);
  const [triggerSearchData, setTriggerSearchData] = useState(false);

  //============================================================================================
  //====================================  PAGINATION START =====================================
  //============================================================================================
  const {
    curPage,
    pageSize,
    total,
    totalPages,
    pageNumbers,
    nextPage,
    prevPage,
    showPage,
    setPageSize,
    setTotal,
    setCurPage,
  } = usePagination();

  useEffect(() => {
    if (initialRender) {
      loadGridData(true, false);
    } else {
      setinitialRender(true);
    }
  }, [curPage, pageSize, triggerSearchData]);

  //============================================================================================
  //====================================  PAGINATION END =======================================
  //============================================================================================

  ////////////-----------------Section For Searching-------------------///////////////////

  type InputChangeEvent = HTMLInputElement | HTMLSelectElement;
  const onInputChangeSearch = (e: React.ChangeEvent<InputChangeEvent>) => {
    setSearchRequest({ ...searchRequest, [e.target.name]: e.target.value });
  };

  function resetSearch() {
    setSearchRequest(initialValues);
    setSorting(sortById);
    loadGridData(true, true);
  }
  ////////////-----------------Section For Searching-------------------///////////////////
  const searchQuery = {
    firstName: searchRequest?.firstName,
    lastName: searchRequest?.lastName,
    npiNumber: searchRequest?.npiNumber,
  };

  ////////////-----------------Get All Data-------------------///////////////////
  const loadGridData = (
    loader: boolean,
    reset: boolean,
    sortingState?: any
  ) => {
    if (loader) {
      setLoading(true);
    }
    const nullobj = {
      firstName: "",
      lastName: "",
      npiNumber: "",
    };
    const obj = {
      pageNumber: curPage,
      pageSize: pageSize,
      queryModel: reset ? nullobj : searchQuery,
      sortColumn: reset ? sortingState?.clickedIconData : sort?.clickedIconData,
      sortDirection: reset ? sortingState?.sortingOrder : sort?.sortingOrder,
    };
    FacilityService.physicianSignatureGetAll({ ...obj })
      .then((res: AxiosResponse) => {
        const list = res?.data?.data;
        setRows(Array.isArray(list) ? list : []);
        setLoading(false);
        setTotal(res?.data?.total ?? 0);
      })
      .catch((err: any) => {
        console.trace(err, "err");
        setRows([]);
        setLoading(false);
      });
  };

  ////////////-----------------Sorting-------------------///////////////////

  const handleSort = (columnName: any) => {
    searchRef.current.id = searchRef.current.id
      ? searchRef.current.id === "asc"
        ? (searchRef.current.id = "desc")
        : (searchRef.current.id = "asc")
      : (searchRef.current.id = "asc");

    setSorting({
      sortingOrder: searchRef?.current?.id,
      clickedIconData: columnName,
    });
  };

  useEffect(() => {
    loadGridData(true, false);
  }, [sort]);

  ////////////-----------------Sorting-------------------///////////////////
  const handleKeyPress = (event: any) => {
    if (event.key === "Enter") {
      loadGridData(true, false);
    }
  };

  return (
    <>
      <div className="app-content flex-column-fluid">
        <div className="app-container container-fluid">
          <div className="card">
            <div className="card-body py-2">
              <div className="d-flex flex-wrap justify-content-center justify-content-sm-between align-items-center">
                <div className="d-flex align-items-center mb-2">
                  <span className="fw-400 mr-3">{t("Records")}</span>
                  <select
                    id="FacilityOptionsRecord"
                    className="form-select w-125px h-33px rounded py-2"
                    data-kt-select2="true"
                    data-placeholder={t("Select option")}
                    data-dropdown-parent="#kt_menu_63b2e70320b73"
                    data-allow-clear="true"
                    onChange={(e) => {
                      setPageSize(parseInt(e.target.value));
                    }}
                  >
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="50" selected>
                      50
                    </option>
                    <option value="100">100</option>
                  </select>
                </div>
                <div className="dalign-items-center d-flex gap-2 gap-lg-3 mb-2 ms-3 ms-sm-0">
                  <button
                    id="FacilityOptionSearch"
                    onClick={() => {
                      setCurPage(1);
                      setTriggerSearchData((prev) => !prev);
                    }}
                    className="btn btn-linkedin btn-sm fw-500"
                    aria-controls="Search"
                  >
                    {t("Search")}
                  </button>
                  <button
                    id="FacilityOptionReset"
                    onClick={resetSearch}
                    type="button"
                    className="btn btn-secondary btn-sm btn-secondary--icon fw-500"
                  >
                    <span>
                      <span>{t("Reset")}</span>
                    </span>
                  </button>
                </div>
              </div>
              <div className="card">
                <Box sx={{ height: "auto", width: "100%" }}>
                  <div className="table_bordered overflow-hidden">
                    <TableContainer
                      sx={{
                        maxHeight: 800,
                        "&::-webkit-scrollbar": {
                          width: 7,
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: "#fff",
                        },
                        "&:hover": {
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "var(--kt-gray-400)",
                            borderRadius: 2,
                          },
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "var(--kt-gray-400)",
                          borderRadius: 2,
                        },
                      }}
                      // component={Paper}
                      className="shadow-none"
                      // sx={{ maxHeight: 'calc(100vh - 100px)' }}
                    >
                      <Table
                        // stickyHeader
                        aria-label="sticky table collapsible"
                        className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
                      >
                        <TableHead>
                          <TableRow className="h-30px">
                            <TableCell></TableCell>
                            {/* <TableCell></TableCell> */}
                            <TableCell>
                              <input
                                id="firstName"
                                type="text"
                                name="firstName"
                                className="form-control bg-white my-2 h-30px rounded-2 fs-8 w-100"
                                placeholder={t("Search ...")}
                                value={searchRequest.firstName}
                                onChange={onInputChangeSearch}
                                onKeyDown={handleKeyPress}
                              />
                            </TableCell>
                            <TableCell>
                              <input
                                id="lastName"
                                type="text"
                                name="lastName"
                                className="form-control bg-white my-2 h-30px rounded-2 fs-8 w-100"
                                placeholder={t("Search ...")}
                                value={searchRequest.lastName}
                                onChange={onInputChangeSearch}
                                onKeyDown={handleKeyPress}
                              />
                            </TableCell>
                            <TableCell>
                              <input
                                id="npiNumber"
                                type="text"
                                name="npiNumber"
                                className="form-control bg-white my-2 h-30px rounded-2 fs-8 w-100"
                                placeholder={t("Search ...")}
                                value={searchRequest.npiNumber}
                                onChange={onInputChangeSearch}
                                onKeyDown={handleKeyPress}
                              />
                            </TableCell>
                          </TableRow>

                          <TableRow className="h-30px">
                            <TableCell className="w-20px min-w-20px"></TableCell>
                            <TableCell
                              className="min-w-150px"
                              sx={{ width: "max-content" }}
                            >
                              <div
                                onClick={() => handleSort("optionName")}
                                className="d-flex justify-content-between cursor-pointer"
                                ref={searchRef}
                              >
                                <div style={{ width: "max-content" }}>
                                  {" "}
                                  {t("First Name")}
                                </div>

                                <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                                  <ArrowUp
                                    CustomeClass={`${
                                      sort.sortingOrder === "desc" &&
                                      sort.clickedIconData === "optionName"
                                        ? "text-success fs-7"
                                        : "text-gray-700 fs-7"
                                    }  p-0 m-0 "`}
                                  />
                                  <ArrowDown
                                    CustomeClass={`${
                                      sort.sortingOrder === "asc" &&
                                      sort.clickedIconData === "optionName"
                                        ? "text-success fs-7"
                                        : "text-gray-700 fs-7"
                                    }  p-0 m-0`}
                                  />
                                </div>
                              </div>
                            </TableCell>
                            <TableCell
                              className="min-w-150px"
                              sx={{ width: "max-content" }}
                            >
                              <div
                                onClick={() => handleSort("optionName")}
                                className="d-flex justify-content-between cursor-pointer"
                                ref={searchRef}
                              >
                                <div style={{ width: "max-content" }}>
                                  {" "}
                                  {t("Last Name")}
                                </div>

                                <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                                  <ArrowUp
                                    CustomeClass={`${
                                      sort.sortingOrder === "desc" &&
                                      sort.clickedIconData === "optionName"
                                        ? "text-success fs-7"
                                        : "text-gray-700 fs-7"
                                    }  p-0 m-0 "`}
                                  />
                                  <ArrowDown
                                    CustomeClass={`${
                                      sort.sortingOrder === "asc" &&
                                      sort.clickedIconData === "optionName"
                                        ? "text-success fs-7"
                                        : "text-gray-700 fs-7"
                                    }  p-0 m-0`}
                                  />
                                </div>
                              </div>
                            </TableCell>
                            <TableCell
                              className="min-w-150px"
                              sx={{ width: "max-content" }}
                            >
                              <div
                                onClick={() => handleSort("optionName")}
                                className="d-flex justify-content-between cursor-pointer"
                                ref={searchRef}
                              >
                                <div style={{ width: "max-content" }}>
                                  {" "}
                                  {t("NPI #")}
                                </div>

                                <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                                  <ArrowUp
                                    CustomeClass={`${
                                      sort.sortingOrder === "desc" &&
                                      sort.clickedIconData === "optionName"
                                        ? "text-success fs-7"
                                        : "text-gray-700 fs-7"
                                    }  p-0 m-0 "`}
                                  />
                                  <ArrowDown
                                    CustomeClass={`${
                                      sort.sortingOrder === "asc" &&
                                      sort.clickedIconData === "optionName"
                                        ? "text-success fs-7"
                                        : "text-gray-700 fs-7"
                                    }  p-0 m-0`}
                                  />
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {loading ? (
                            <TableCell colSpan={4} className="padding-0">
                              <Loader />
                            </TableCell>
                          ) : (
                            (rows ?? []).map((item: any, index) => {
                              return (
                                <Row
                                  key={item?.id ?? index}
                                  row={item}
                                  loadGridData={loadGridData}
                                />
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </Box>
              </div>

              {/* ==========================================================================================
              //====================================  PAGINATION START =====================================
              //============================================================================================ */}
              <CustomPagination
                curPage={curPage}
                nextPage={nextPage}
                pageNumbers={pageNumbers}
                pageSize={pageSize}
                prevPage={prevPage}
                showPage={showPage}
                total={total}
                totalPages={totalPages}
              />
              {/* ==========================================================================================
              //====================================  PAGINATION END =====================================
              //============================================================================================ */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
